import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUID } from '@/utils';
import { getBettingHistory } from '@/services/sports/bettingService';
import { ErrorStatsvgIcon } from '@/components/icons';
import styles from './MyBets.module.css';

/**
 * MyBets Component
 * Displays betting history with filter tabs
 * Uses React Query for optimized data fetching
 * Uses theme colors from variables.css
 */
const MyBets = () => {
  const [activeTab, setActiveTab] = useState('All');
  const uid = getUID();

  // Fetch betting history using React Query
  const {
    data: bettingData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['bettingHistory', uid],
    queryFn: () => getBettingHistory(uid),
    enabled: !!uid, // Only fetch if uid exists
    staleTime: 30000, // 30 seconds - betting history changes frequently
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    retry: 2,
    retryDelay: 1000,
  });

  const bets = bettingData?.bets || [];

  // Filter bets based on active tab
  const filteredBets = useMemo(() => {
    if (activeTab === 'All') return bets;

    const statusMap = {
      'Open': 'open',
      'Won': 'won',
      'Lost': 'lost',
      'Cashed Out': 'cashed_out',
      'Cancelled': 'cancelled',
      'Refund': 'refund',
    };

    const targetStatus = statusMap[activeTab]?.toLowerCase();
    if (!targetStatus) return bets;

    return bets.filter((bet) => {
      const betStatus = (bet.status || '').toLowerCase();
      return betStatus === targetStatus;
    });
  }, [bets, activeTab]);

  // Count bets by status
  const betCounts = useMemo(() => {
    const counts = {
      All: bets.length,
      Open: 0,
      Won: 0,
      Lost: 0,
      'Cashed Out': 0,
      Cancelled: 0,
      Refund: 0,
    };

    bets.forEach((bet) => {
      const status = (bet.status || '').toLowerCase();
      if (status === 'open') counts.Open++;
      else if (status === 'won') counts.Won++;
      else if (status === 'lost') counts.Lost++;
      else if (status === 'cashed_out' || status === 'cashedout') counts['Cashed Out']++;
      else if (status === 'cancelled') counts.Cancelled++;
      else if (status === 'refund') counts.Refund++;
    });

    return counts;
  }, [bets]);

  const tabs = ['All', 'Open', 'Won', 'Lost', 'Cashed Out', 'Cancelled', 'Refund'];

  // Format date for better mobile display
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days < 7) return `${days}d ago`;

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } catch {
      return dateString;
    }
  };

  // Only show loader on initial load (when no data exists yet)
  // Show data instantly when available
  if (isLoading && !bettingData) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loader}>
          <span className={styles.loaderText}>loading</span>
          <span className={styles.load}></span>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorMessage}>
          <p>Error loading betting history</p>
          <button className={styles.retryButton} onClick={() => refetch()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Handle no user ID
  if (!uid) {
    return (
      <>
        <ErrorStatsvgIcon size="152" className={styles.emptyBetsIcon} />
        <h3 className={styles.emptyTitle}>No bets are available</h3>
        <p className={styles.emptySubtitle}>Choose different dates</p>


      </>
    );
  }

  return (
    <div className={styles.myBetsContainer}>
      {/* Tabs */}
      <div className={styles.tabsContainer}>
        {tabs.map((tab) => (
          <button
            key={tab}
            className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            <span className={styles.tabText}>{tab}</span>
            {betCounts[tab] > 0 && (
              <span className={styles.tabBadge}>{betCounts[tab]}</span>
            )}
          </button>
        ))}
      </div>

      {/* Betting Table */}
      <div className={styles.tableWrapper}>
        {filteredBets.length > 0 ? (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Game</th>
                <th>Match</th>
                <th>Selection</th>
                <th>Type</th>
                <th>Odds</th>
                <th>Stake</th>
                <th>Liability</th>
                <th>Status</th>
                <th>Result</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredBets.map((bet, index) => {
                const gameType = bet.game_type || 'Unknown';
                const matchTitle = bet.match_title || bet.match_id || 'Unknown Match';
                const betType = bet.bet_type || 'fancy';
                const status = (bet.status || '').toLowerCase();
                const date = formatDate(bet.match_start_time || bet.created_at);

                return (
                  <tr
                    key={bet.id || `${matchTitle}-${bet.selection_name}-${index}`}
                    className={`${styles.tableRow} ${styles[`status${status.charAt(0).toUpperCase() + status.slice(1)}`] || ''}`}
                  >
                    <td data-label="Game">{matchTitle}</td>
                    <td data-label="Match">({gameType})</td>
                    <td data-label="Selection">{bet.selection_name || 'N/A'}</td>
                    <td data-label="Type">
                      <span className={`${styles.betType} ${styles[`type${betType}`] || ''}`}>
                        {betType === 'back' && <span className={styles.back}>Back</span>}
                        {betType === 'lay' && <span className={styles.lay}>Lay</span>}
                        {betType === 'fancy' && <span>Fancy</span>}
                      </span>
                    </td>
                    <td data-label="Odds">{bet.odds ?? 'N/A'}</td>
                    <td data-label="Stake">₹{bet.stake ?? 'N/A'}</td>
                    <td data-label="Liability">₹{bet.liability ?? 'N/A'}</td>
                    <td data-label="Status">
                      <span className={`${styles.status} ${styles[`status${status.charAt(0).toUpperCase() + status.slice(1)}`] || ''}`}>
                        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
                      </span>
                    </td>
                    <td data-label="Result">{bet.result_status ?? '—'}</td>
                    <td data-label="Date">{date}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className={styles.emptyState}>
            {/* <div className={styles.emptyIcon}>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4"/>
                <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="18" cy="6" r="1.5" fill="currentColor"/>
                <circle cx="18" cy="18" r="1.5" fill="currentColor"/>
              </svg>
            </div> */}
            <ErrorStatsvgIcon size="152" className={styles.emptyBetsIcon} />
            <h3 className={styles.emptyTitle}>No bets are available</h3>
            <p className={styles.emptySubtitle}>Choose different dates</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBets;
